/**
 * Footer Component
 * 
 * Site footer with dynamic contact info fetched from Settings API.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  PhoneIcon, 
  EnvelopeIcon, 
  MapPinIcon,
  ClockIcon 
} from '@heroicons/react/24/outline';
import { FaInstagram, FaFacebook } from 'react-icons/fa';
import { useSiteImageOverrides } from '../hooks/useSiteImageOverrides';
import UberEatsOrderLink from './UberEatsOrderLink';
import { BAKERY_LOCAL } from '../utils/bakeryLocal';

interface BusinessHours {
  day: string;
  open: string;
  close: string;
  isClosed: boolean;
}

interface ContactInfo {
  phone: string;
  email: string;
  address: string;
}

// Format time from 24h to 12h format
function formatTime(time: string): string {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  const ampm = hours >= 12 ? 'pm' : 'am';
  const hour12 = hours % 12 || 12;
  return `${hour12}${minutes ? ':' + minutes.toString().padStart(2, '0') : ''}${ampm}`;
}

export default function Footer() {
  const { getImage } = useSiteImageOverrides();
  const currentYear = new Date().getFullYear();
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    phone: '(705) 503-4966',
    email: 'nannybbakedgoods@outlook.com',
    address: '62 Commerce Park Drive, Unit L, Barrie, ON L4N 8W8',
  });
  const [businessHours, setBusinessHours] = useState<BusinessHours[]>([]);

  // Fetch dynamic settings on mount
  useEffect(() => {
    // Fetch contact info
    fetch('/api/settings/contact')
      .then((res) => res.json())
      .then((data) => {
        if (data.phone || data.email || data.address) {
          setContactInfo(data);
        }
      })
      .catch((err) => console.error('Failed to fetch contact info:', err));

    // Fetch business hours
    fetch('/api/settings/hours')
      .then((res) => res.json())
      .then((data) => {
        if (data.hours && Array.isArray(data.hours)) {
          setBusinessHours(data.hours);
        }
      })
      .catch((err) => console.error('Failed to fetch hours:', err));
  }, []);

  // Format address for display (split at comma)
  const addressLines = contactInfo.address.split(', ');
  const addressLine1 = addressLines.slice(0, 2).join(', ');
  const addressLine2 = addressLines.slice(2).join(', ');

  // Group hours for display
  const formatHoursForFooter = () => {
    if (businessHours.length === 0) {
      return [
        { label: 'Tue-Fri', hours: '11am - 4:30pm' },
        { label: 'Sat', hours: '10am - 3pm' },
        { label: 'Sun-Mon', hours: 'Closed' },
      ];
    }

    const result: { label: string; hours: string }[] = [];
    let i = 0;

    while (i < businessHours.length) {
      const current = businessHours[i];
      let endIndex = i;

      // Find consecutive days with same hours
      while (
        endIndex < businessHours.length - 1 &&
        businessHours[endIndex + 1].isClosed === current.isClosed &&
        businessHours[endIndex + 1].open === current.open &&
        businessHours[endIndex + 1].close === current.close
      ) {
        endIndex++;
      }

      const startDay = current.day.substring(0, 3);
      const endDay = businessHours[endIndex].day.substring(0, 3);
      const dayRange = startDay === endDay ? startDay : `${startDay}-${endDay}`;

      result.push({
        label: dayRange,
        hours: current.isClosed ? 'Closed' : `${formatTime(current.open)} - ${formatTime(current.close)}`,
      });

      i = endIndex + 1;
    }

    return result;
  };

  const hoursDisplay = formatHoursForFooter();

  return (
    <footer className="bg-gray-900 text-white">
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* About */}
          <div>
            <img 
              src={getImage('logo', '/assets/images/branding/logo2.webp')} 
              alt="Nanny B's Bakery Logo" 
              className="h-16 w-auto mb-4"
            />
            <p className="text-gray-300 mb-4">
              Fresh baked goods, custom cakes, and delicious treats made with love in Barrie, Ontario.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.instagram.com/nannybbakery/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-400 transition-colors"
                aria-label="Instagram"
              >
                <FaInstagram className="h-6 w-6" />
              </a>
              <a
                href="https://www.facebook.com/profile.php/?id=100086623726840"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-400 transition-colors"
                aria-label="Facebook"
              >
                <FaFacebook className="h-6 w-6" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-300 hover:text-primary-400 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/menu" className="text-gray-300 hover:text-primary-400 transition-colors">
                  Menu
                </Link>
              </li>
              <li>
                <Link to="/gallery" className="text-gray-300 hover:text-primary-400 transition-colors">
                  Gallery
                </Link>
              </li>
              <li>
                <Link to="/guides" className="text-gray-300 hover:text-primary-400 transition-colors">
                  Guides
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-300 hover:text-primary-400 transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/bakery-in-barrie" className="text-gray-300 hover:text-primary-400 transition-colors">
                  Bakery in Barrie
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-primary-400 transition-colors">
                  Contact
                </Link>
              </li>
              <li>
                <UberEatsOrderLink variant="footer" />
              </li>
            </ul>
          </div>

          {/* Contact Info - Dynamic */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPinIcon className="h-5 w-5 text-primary-400 mt-0.5 flex-shrink-0" />
                <a 
                  href={BAKERY_LOCAL.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-primary-400 transition-colors"
                >
                  {addressLine1}<br />
                  {addressLine2}
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <PhoneIcon className="h-5 w-5 text-primary-400 flex-shrink-0" />
                <a 
                  href={`tel:${contactInfo.phone.replace(/\D/g, '')}`} 
                  className="text-gray-300 hover:text-primary-400 transition-colors"
                >
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <EnvelopeIcon className="h-5 w-5 text-primary-400 flex-shrink-0" />
                <a 
                  href={`mailto:${contactInfo.email}`} 
                  className="text-gray-300 hover:text-primary-400 transition-colors"
                >
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <ClockIcon className="h-5 w-5 text-primary-400 mt-0.5 flex-shrink-0" />
                <div className="text-gray-300">
                  <p className="font-semibold mb-1">Hours:</p>
                  {hoursDisplay.map((item, idx) => (
                    <p key={idx}>{item.label}: {item.hours}</p>
                  ))}
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
          <p>&copy; {currentYear} Nanny B&apos;s Bakery. All rights reserved.</p>
          <p className="mt-2">
            <Link to="/privacy" className="hover:text-primary-400 transition-colors">
              Privacy Policy
            </Link>
            {' · '}
            <Link to="/pickup-and-returns" className="hover:text-primary-400 transition-colors">
              Pickup &amp; returns
            </Link>
          </p>
        </div>
      </div>
    </footer>
  );
}
